import { AnalyticsEventName } from '@dupe/common/constants'
import { LoaderIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import Button from '~/src/components/Button'
import { Input } from '~/src/components/ui/input'
import useAnalytics from '~/src/hooks/useAnalytics'
import {
  clearStoredInviteCode,
  getStoredInviteCode,
} from '~/src/hooks/useInviteCodeFromUrl'
import { useProcessInvite } from '~/src/hooks/useProcessInvite'
import { useValidateInvite } from '~/src/hooks/useValidateInvite'
import { cn } from '~/src/lib/utils'
import {
  OnboardingStep,
  useOnboardingActions,
} from '~/src/stores/useOnboardingStore'

const isValidInviteFormat = (code: string) => {
  // Check if it matches XX-XXXXX format (2 letters, dash, 5 numbers)
  const regex = /^[A-Z]{2}-[0-9]{5}$/
  return regex.test(code)
}

const InviteCodeStep: React.FC = () => {
  const analytics = useAnalytics()
  const [inviteCode, setInviteCode] = useState(() =>
    (getStoredInviteCode() || '').trim()
  )
  const [hasValidFormat, setHasValidFormat] = useState(false)
  const [isProcessingInvite, setIsProcessingInvite] = useState(false)
  const [inviteProcessed, setInviteProcessed] = useState(false)

  const { nextStep, markStepComplete } = useOnboardingActions()
  const { mutate: processInvite } = useProcessInvite()

  const isValidFormat = isValidInviteFormat(inviteCode)

  // Validate invite code when user enters it (only if feature is enabled)
  const shouldValidate = hasValidFormat && inviteCode && isValidFormat

  const { data: inviteData } = useValidateInvite(
    shouldValidate ? inviteCode : null
  )

  const shouldAutoSubmit =
    inviteCode && isValidFormat && hasValidFormat && inviteData?.valid

  // const preProcessing = isFeatureLoading

  // Only formats the input as the user types or removes values
  const handleInviteCodeKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    const value = input.value.trim().toUpperCase()
    const key = e.key

    // Allow navigation keys, backspace, delete, etc.
    if (
      [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Home',
        'End',
      ].includes(key)
    ) {
      return
    }

    // Remove all non-alphanumeric characters
    const cleaned = (value || '').replace(/[^A-Z0-9]/g, '')
    const limited = cleaned.slice(0, 7)
    let formatted = limited

    if (limited.length >= 2) {
      formatted = `${limited.slice(0, 2)}-${limited.slice(2)}`
    }

    // Prevent entering more than 8 characters (including dash)
    if (input.selectionStart !== null && input.selectionEnd !== null) {
      const nextValue =
        value.slice(0, input.selectionStart) +
        key +
        value.slice(input.selectionEnd)

      if (nextValue.replace(/[^A-Z0-9]/g, '').length > 7) {
        e.preventDefault()
        return
      }
    }

    // Set the formatted value
    setTimeout(() => {
      input.value = formatted
    }, 0)
  }

  // Handles validation and state updates
  const handleInviteCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = e.target.value.trim()
    setInviteCode(formatted)
    setHasValidFormat(false)
    setInviteProcessed(false)

    // Auto-validate when format is complete
    if (isValidInviteFormat(formatted)) {
      setHasValidFormat(true)
    }
  }

  const handleValidateCode = () => {
    if (inviteCode && isValidInviteFormat(inviteCode)) {
      setHasValidFormat(true)
    }
  }

  const handleAcceptInvite = useCallback(async () => {
    if (!inviteData?.valid) return

    setIsProcessingInvite(true)

    try {
      const response = await new Promise<{ success: boolean; error?: string }>(
        (resolve, reject) => {
          processInvite(
            { inviteCode: inviteCode },
            {
              onSuccess: () => {
                setInviteProcessed(true)
                resolve({
                  success: true,
                })
              },
              onError: (error) => {
                console.error('Failed to process invite code:', error)
                reject({ error })
              },
            }
          )
        }
      )

      // Success - track analytics and move to next step
      analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
        step: 'invite-code',
        hasInviteCode: true,
        inviteCodeValid: true,
        processed: true,
        autoSubmit: shouldAutoSubmit,
        response,
      })
    } catch (error) {
      // Error - track analytics but don't navigate
      analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
        step: 'invite-code',
        hasInviteCode: true,
        inviteCodeValid: true,
        processed: false,
        autoSubmit: shouldAutoSubmit,
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      setIsProcessingInvite(false)

      // Clear stored invite code since it was used successfully
      clearStoredInviteCode()

      // Mark step complete and move to next step
      markStepComplete(OnboardingStep.INVITE_CODE)
      nextStep(OnboardingStep.COMPLETE)
    }
  }, [
    analytics,
    inviteCode,
    inviteData?.valid,
    markStepComplete,
    nextStep,
    processInvite,
    shouldAutoSubmit,
  ])

  const handleContinue = () => {
    analytics.track(AnalyticsEventName.AuthModalOnboardingSubmit, {
      step: 'invite-code',
      inviteCode,
      hasInviteCode: !!inviteCode,
      inviteCodeValid: !!inviteData?.valid,
      processed: inviteProcessed,
      autoSubmit: shouldAutoSubmit,
      skip: true,
    })

    // Mark step complete and continue
    markStepComplete(OnboardingStep.INVITE_CODE)
    nextStep(OnboardingStep.COMPLETE)
  }

  // Auto-validate stored code on component mount
  useEffect(() => {
    const storedCode = getStoredInviteCode()
    if (storedCode && isValidInviteFormat(storedCode)) {
      setHasValidFormat(true)
    }
  }, [])

  // Also ensure validation runs when inviteCode changes and is valid format
  useEffect(() => {
    if (inviteCode && isValidFormat && !hasValidFormat) {
      setHasValidFormat(true)
    }
  }, [inviteCode, hasValidFormat, isValidFormat])

  // on mount, if we have a valid invite code, just
  // process it and move to next step
  useEffect(() => {
    if (isProcessingInvite) return
    if (shouldAutoSubmit) {
      handleAcceptInvite()
    }
  }, [handleAcceptInvite, shouldAutoSubmit, isProcessingInvite])

  if (shouldAutoSubmit) return null

  return (
    <div className='space-y-6'>
      <div className='space-y-1 text-center'>
        <div className='text-xl font-semibold md:text-3xl'>
          Got an invite code?
        </div>
        <div className='opacity-50'>Help your friend earn points!</div>
      </div>

      <div className='space-y-2'>
        <div className='relative'>
          <Input
            type='text'
            placeholder='Example: AB-12345'
            value={inviteCode}
            onChange={handleInviteCodeChange}
            onKeyUp={handleInviteCodeKeyUp}
            onBlur={handleValidateCode}
            className={cn(
              'h-14 rounded-full text-center font-mono text-lg tracking-widest placeholder:text-black/40',
              {
                'border-red-500 focus:border-red-500':
                  isValidFormat && inviteData && !inviteData?.valid,
              }
            )}
            maxLength={8} // XX-12345 = 8 characters
            autoFocus
          />
          {isValidFormat && inviteData && !inviteData?.valid && (
            <div className='pointer-events-none absolute inset-x-0 bottom-full -mb-1.5 flex items-center justify-center'>
              <div className='rounded-full bg-red-500 px-2 py-1 text-xs font-semibold text-white'>
                {inviteData.reason}
              </div>
              <div
                className='absolute bottom-[-8px] left-1/2 h-0 w-0 -translate-x-1/2
                border-x-8 border-t-8 border-x-transparent border-t-red-500'
                aria-hidden='true'
              />
            </div>
          )}
        </div>

        {isValidFormat && inviteData?.valid ? (
          <div className='flex flex-col items-center justify-center space-y-6'>
            <Button
              type='button'
              disabled={isProcessingInvite || !isValidFormat}
              size='lg'
              fullWidth
              rounded
              onClick={handleAcceptInvite}
            >
              {isProcessingInvite ? (
                <div className='animate-spin'>
                  <LoaderIcon height={18} />
                </div>
              ) : (
                'Continue'
              )}
            </Button>
          </div>
        ) : (
          <div>
            <button
              onClick={handleContinue}
              className='mt-4 w-full text-black underline opacity-40 transition-opacity hover:opacity-100'
            >
              Continue without invite code
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default InviteCodeStep
